/**
 * Web Mercator tile maths for the campground tile endpoint.
 *
 * Pure: no React, no fetching, no MapLibre types. The viewport comes in as
 * plain numbers so this can be tested without a map.
 */

export type TileCoord = { z: number; x: number; y: number };

/**
 * How far below the map's zoom to cut tiles.
 *
 * Two levels down puts a ~1200px viewport across roughly four tiles, each a
 * little smaller than the viewport. Going coarser simulates faster but makes
 * each tile cover ~3x the viewport, which stretches the server's per-tile cap
 * over far more ground than it can honestly represent -- measured at z=7 a
 * production tile holds a median of 484 campgrounds against a 400 cap, while
 * at z=8 it holds 156.
 */
export const TILE_ZOOM_OFFSET = 2;

// Mirrored independently in backend/api/internal/utils/tiles.py (no shared
// constant crosses the language boundary). If the two ever drift, this can
// request a zoom the server 404s on -- keep them equal.
export const MAX_TILE_ZOOM = 14;

// Web Mercator is undefined at the poles and is truncated here.
const LAT_LIMIT = 85.0511287798066;

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

export function tileZoomFor(mapZoom: number): number {
  return clamp(Math.round(mapZoom) - TILE_ZOOM_OFFSET, 0, MAX_TILE_ZOOM);
}

function lonToTileX(lon: number, n: number): number {
  const x = Math.floor(((lon + 180) / 360) * n);
  // MapLibre keeps counting longitude past ±180 as the user pans, so x can
  // fall outside [0, n). Wrapping is correct -- it is the same column of the
  // world -- and the server rejects out-of-range x, so this must happen here.
  return ((x % n) + n) % n;
}

function latToTileY(lat: number, n: number): number {
  const clamped = clamp(lat, -LAT_LIMIT, LAT_LIMIT);
  const rad = (clamped * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.asinh(Math.tan(rad)) / Math.PI) / 2) * n
  );
  // A viewport at the very top or bottom can land exactly on n.
  return clamp(y, 0, n - 1);
}

/**
 * The single tile a point falls in, at the level derived from the map zoom.
 *
 * The same arithmetic tilesForBounds walks, for the one-point case: which tile
 * am I looking at.
 */
export function tileForPoint(
  point: { lat: number; lng: number },
  mapZoom: number
): TileCoord {
  const z = tileZoomFor(mapZoom);
  const n = 1 << z;
  return { z, x: lonToTileX(point.lng, n), y: latToTileY(point.lat, n) };
}

/**
 * The geographic box a tile covers -- the inverse of the lonToTileX and
 * latToTileY arithmetic above, for drawing tile boundaries on a map.
 */
export function tileBounds(tile: TileCoord): {
  west: number;
  south: number;
  east: number;
  north: number;
} {
  const n = 1 << tile.z;
  const lonAt = (x: number) => (x / n) * 360 - 180;
  // Inverse of the Mercator y: asinh(tan(lat)) = pi * (1 - 2y/n).
  const latAt = (y: number) =>
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;

  return {
    west: lonAt(tile.x),
    east: lonAt(tile.x + 1),
    north: latAt(tile.y),
    south: latAt(tile.y + 1),
  };
}

/**
 * Every tile the viewport touches, at the level derived from the map zoom.
 */
export function tilesForBounds(
  bounds: { west: number; south: number; east: number; north: number },
  mapZoom: number
): TileCoord[] {
  const z = tileZoomFor(mapZoom);
  const n = 1 << z;

  const yTop = latToTileY(bounds.north, n);
  const yBottom = latToTileY(bounds.south, n);

  // Walk the unwrapped x range so a viewport spanning the date line still
  // yields a contiguous run, then wrap each column as it is emitted.
  const xStart = Math.floor(((bounds.west + 180) / 360) * n);
  const xEnd = Math.floor(((bounds.east + 180) / 360) * n);
  // An inverted box (east < west) would make this negative, and the loop
  // below would then emit nothing -- silently reporting a successful empty
  // search rather than the outage or bad-input this actually is. MapLibre's
  // mercator getBounds() never hands this function west > east, but that is
  // the caller's behaviour, not this exported pure function's promise to
  // keep, so clamp to at least one column instead.
  const span = Math.min(Math.max(xEnd - xStart, 0), n - 1);

  const seen = new Set<string>();
  const tiles: TileCoord[] = [];
  for (let i = 0; i <= span; i++) {
    const x = lonToTileX(((xStart + i) / n) * 360 - 180 + 1e-9, n);
    for (let y = Math.min(yTop, yBottom); y <= Math.max(yTop, yBottom); y++) {
      const k = `${z}/${x}/${y}`;
      if (seen.has(k)) continue;
      seen.add(k);
      tiles.push({ z, x, y });
    }
  }
  return tiles;
}
